import React, { useState, useEffect, useRef } from "react";
import { Get } from "../../Common/WebRequest";
import {
  Box,
  FormControl,
  Typography,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import { generateUUID, getValue } from "../../Common/Utility";
import columnBuilder from "./columnBuilder";
import { DataGrid } from "@mui/x-data-grid";
import DropDownBranch from "../../components/DropDown/Branch/DropDownBranch";
import { blue } from "@mui/material/colors";
import DropDownRegion from "../../components/DropDown/Region/DropDownRegion";
import { WebRequestPdfViewer } from "../../Common/WebRequestPdfViewer";
import SyncIcon from "@mui/icons-material/Sync";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import { handlePdfHistoryExport } from "./RowsExport";
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";

export const PdfFilesHistory = () => {
  const [regionId, setRegionId] = useState(-1);
  const [branchId, setBranchId] = useState(-1);
  const [error, setError] = useState("");
  const [pdfHistorysData, setPdfHistorysData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef(null);

  let withAll = true;

  const fetchData = async () => {
    try {
      let res = await Get(
        "Calculation/PdfHistory/" + regionId + "/" + branchId
      );
      if (res.isSuccess) {
        // Store the original unfiltered data
        setAllData(res.result);
        // Apply initial branch filter
        const filteredData = doFilter(res.result);
        setPdfHistorysData(filteredData);
      } else {
        setError(res.Status + "/" + res.Error);
      }
    } catch (err) {
      setError("Failed to fetch PDF history data");
    }
  };

  useEffect(() => {
    fetchData();
  }, [regionId, branchId]);

  // Custom search function for the DataGrid
  const customSearchFunction = (searchValue) => {
    if (!searchValue) return pdfHistorysData;

    return pdfHistorysData.filter((row) => {
      // Check if any field contains the search value
      return Object.keys(row).some((key) => {
        const value = row[key];

        // Special handling for date fields
        if (key === "pdfDate") {
          const dateValue = value ? String(value) : "";
          return dateValue.toLowerCase().includes(searchValue.toLowerCase());
        }

        // For other fields, use the default contains behavior
        return String(value).toLowerCase().includes(searchValue.toLowerCase());
      });
    });
  };

  // Filtered data based on search value
  const filteredData = searchValue
    ? customSearchFunction(searchValue)
    : pdfHistorysData;

  // Effect to maintain focus on the search input field
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchValue]);

  function doFilter(res) {
    const filteredPdfHistories =
      branchId === -1
        ? res
        : res.filter((students) => students.branchId === branchId);

    return filteredPdfHistories;
  }

  async function handleOnCellClick(params) {
    if (params.field === "pdfFileName") {
      let fileName = params.value;
      if (fileName?.length > 0) {
        let props = { path: "Calculation/pdf/" + fileName };
        await WebRequestPdfViewer(props);
      }
    }
  }

  function handleRegionValue(e) {
    const newRegionId = getValue(e.target.value);
    setRegionId(newRegionId);
    if (withAll) {
      setBranchId(-1);
    }
    setPdfHistorysData([]);
    setError("");
  }

  function handleBranchValue(e) {
    const newBranchId = getValue(e.target.value);
    setBranchId(newBranchId);
    setPdfHistorysData([]);
    setError("");
  }

  const handleQuickFilterChange = (filterModel) => {
    const searchValue = filterModel.quickFilterValues?.[0]?.toLowerCase() || "";
    setSearchText(searchValue);

    if (!searchValue) {
      setPdfHistorysData(doFilter(allData));
      return;
    }

    const branchFiltered = doFilter(allData);
    const filtered = branchFiltered.filter((row) =>
      Object.values(row).some((value) =>
        value?.toString().toLowerCase().includes(searchValue)
      )
    );

    setPdfHistorysData(filtered);
  };

  function handleExportClick(format) {
    handlePdfHistoryExport(filteredData, format);
  }

  function handleRefresh() {
    window.location.reload();
  }

  function DefinedToolbar() {
    return (
      <Box
        sx={{
          p: 0.5,
          pb: 0,
        }}
      >
        <GridToolbarContainer>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          <Button
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={() => handleExportClick("csv")}
          >
            Export CSV
          </Button>
          <Button
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={() => handleExportClick("pdf")}
            sx={{ ml: 1 }}
          >
            Export PDF
          </Button>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            inputRef={searchInputRef}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: 200, ml: 1 }}
          />
          <Button
            color="primary"
            startIcon={<SyncIcon />}
            onClick={handleRefresh}
            sx={{ ml: 1 }}
          >
            Refresh
          </Button>
        </GridToolbarContainer>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 800,
        width: "95%",
        "& .actions": {
          color: "text.secondary",
        },
        "& .textPrimary": {
          color: "text.primary",
        },
      }}
    >
      <Typography
        variant="h6"
        component="h6"
        sx={{ textAlign: "left", color: blue[700] }}
      >
        PDF-Dateien
      </Typography>

      <FormControl sx={{ m: 1, minWidth: 172 }}>
        <DropDownRegion
          id="region-select"
          value={regionId}
          onChange={handleRegionValue}
          withAll={withAll}
        />
      </FormControl>

      <FormControl sx={{ m: 1, minWidth: 172 }}>
        <DropDownBranch
          id="branch-select"
          value={branchId}
          onChange={handleBranchValue}
          regionId={regionId}
          withAll={withAll}
        />
      </FormControl>

      <DataGrid
        rowSelection={false}
        density="compact"
        rows={filteredData}
        columns={columnBuilder()}
        onCellClick={handleOnCellClick}
        rowsPerPageOptions={10}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 100, page: 0 },
          },
        }}
        slots={{ toolbar: DefinedToolbar }}
        onFilterModelChange={handleQuickFilterChange}
        getRowId={(row) => generateUUID()}
      />
    </Box>
  );
};

export default PdfFilesHistory;
